import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClassroomContext } from 'contexts/ClassroomContext';
import Assignment from 'models/Assignment';
import teacherService from 'services/teacherService';
import TeacherGradebookEntry from 'models/TeacherGradebookEntry';
import { DisplayMissedWord, ServerMissedWords } from 'utils/types';
import { AppPage } from 'view/components/common/AppPage';
import useApiCall from 'contexts/ApiCall';
import Modal from 'view/components/common/Modal';
import GeneratePdfPopup from 'view/components/teacher/analytics/GeneratePdfPopup';
import AssignmentAnayltics from 'view/components/teacher/analytics/AssignmentAnalytics';
import ClassroomAnalytics from 'view/components/teacher/analytics/ClassroomAnalytics';
import SubmissionAnalytics from 'view/components/teacher/analytics/SubmissionAnalytics';
import StudentAnalytics from 'view/components/teacher/analytics/student/StudentAnalytics';
import AnalyticsSidebar from 'view/components/teacher/analytics/AnalyticsSidebar';
import StudentMetrics from 'models/analytics/StudentMetrics';
import { useUserClassrooms } from 'contexts/UserClassroomsContext';
import Classroom from 'models/Classroom';
import { countMissedWords } from 'utils/analytics';
import { useUserContext } from 'contexts/UserContext';
import { districtAdminService } from 'services/districtAdminService';
import DistrictGradebook from 'models/analytics/DistrictGradebook';
import DistrictAnalytics from 'view/components/admin/analytics/DistrictAnalytics';
import TeacherGradebook from 'models/TeacherGradebook';

import 'view/style/teacher/analytics.css';

export const defaultMissedWordChartData = [
  {
    label: 'Known',
    data: [],
    backgroundColor: 'rgba(68, 125, 0, 1)',
  },
  {
    label: 'Unknown',
    data: [],
    backgroundColor: 'rgba(177, 36, 63, 1)',
  },
];

export type ChartData = {
  label: string;
  data: number[];
  backgroundColor: string;
};

enum AnaltyicsType {
  CLASSROOM = 'classroom',
  ASSIGNMENT = 'assignment',
  STUDENT = 'student',
  SUBMISSION = 'studentAssignment',
  DISTRICT = 'district',
}

const DistrictAdminAnalyticsPage: React.FC = () => {
  const { classroom, setClassroom } = useClassroomContext();
  const { user } = useUserContext();
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment>();
  const [selectedStudent, setSelectedStudent] =
    useState<TeacherGradebookEntry>();
  const [gradebook, setGradebook] = useState<DistrictGradebook>();
  const [missedWords, setMissedWords] = useState<ServerMissedWords>();
  const [averages, setAverages] = useState<Record<string, any>>();
  const [showPdfPopup, setShowPdfPopup] = useState<boolean>(false);
  const [analyticsType, setAnalyticsType] = useState<AnaltyicsType>(
    AnaltyicsType.CLASSROOM,
  );
  const [studentMetrics, setStudentMetrics] = useState<StudentMetrics>();
  const { classrooms } = useUserClassrooms();
  const [searchParams, setSearchParams] = useSearchParams();
  const [classGradebook, setClassGradebook] = useState<TeacherGradebook>();
  const makeApiCall = useApiCall();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.district) return;
    makeApiCall(districtAdminService.getGradebook, user!.district!.id)
      .then((respGradebook) => setGradebook(respGradebook))
      .catch((error) => alert(error.message));
    // eslint-disable-next-line
  }, [user?.district, makeApiCall, setGradebook]);

  useEffect(() => {
    if (!classroom) {
      setClassGradebook(undefined);
      return;
    }

    makeApiCall(teacherService.getClassroomMissedWords, classroom)
      .then((respWords) => setMissedWords(respWords))
      .catch((error) => alert(error.message));
    makeApiCall(teacherService.getStudentMetrics, classroom)
      .then((respMetrics) => setStudentMetrics(respMetrics))
      .catch((error) => alert(error.message));
    makeApiCall(teacherService.getGradebook, classroom.id).then((resp) =>
      setClassGradebook(resp),
    );
  }, [classroom, makeApiCall]);

  useEffect(() => {
    var newAnalyticsType;
    if (classroom && selectedAssignment && selectedStudent) {
      newAnalyticsType = AnaltyicsType.SUBMISSION;
    } else if (classroom && selectedAssignment) {
      newAnalyticsType = AnaltyicsType.ASSIGNMENT;
    } else if (classroom && selectedStudent) {
      newAnalyticsType = AnaltyicsType.STUDENT;
    } else if (classroom) {
      newAnalyticsType = AnaltyicsType.CLASSROOM;
    } else {
      newAnalyticsType = AnaltyicsType.DISTRICT;
    }
    setAnalyticsType(newAnalyticsType);

    if (newAnalyticsType === AnaltyicsType.DISTRICT) {
      setAverages({
        grade: gradebook?.averages.grade,
        completion_score: gradebook?.averages.completion_score,
        correctness_score: gradebook?.averages.correctness_score,
        key_word_accuracy_score: gradebook?.averages.key_word_accuracy_score,
        completion_time: gradebook?.averages.completion_time,
        missed_words: 0,
      });
    } else if (newAnalyticsType === AnaltyicsType.CLASSROOM) {
      setAverages({
        grade: gradebook?.entries[classroom!.id]?.grade,
        completion_score: gradebook?.entries[classroom!.id]?.completion_score,
        correctness_score: gradebook?.entries[classroom!.id]?.correctness_score,
        key_word_accuracy_score:
          gradebook?.entries[classroom!.id]?.key_word_accuracy_score,
        completion_time: gradebook?.entries[classroom!.id]?.completion_time,
        missed_words: 0,
      });
    } else if (newAnalyticsType === AnaltyicsType.ASSIGNMENT) {
      setAverages({
        grade:
          gradebook?.entries[classroom!.id]?.assignments[
            selectedAssignment!.getId()
          ].grade,
        completion_score:
          gradebook?.entries[classroom!.id]?.assignments[
            selectedAssignment!.getId()
          ].completion_score,
        correctness_score:
          gradebook?.entries[classroom!.id]?.assignments[
            selectedAssignment!.getId()
          ].correctness_score,
        key_word_accuracy_score:
          gradebook?.entries[classroom!.id]?.assignments[
            selectedAssignment!.getId()
          ].key_word_accuracy_score,
        completion_time:
          gradebook?.entries[classroom!.id]?.assignments[
            selectedAssignment!.getId()
          ].completion_time,
        missed_words: 0,
      });
    } else if (newAnalyticsType === AnaltyicsType.STUDENT) {
      setAverages({
        grade: selectedStudent!.averages.grade,
        completion_score: selectedStudent!.averages.completion_score,
        correctness_score: selectedStudent!.averages.correctness_score,
        key_word_accuracy_score:
          selectedStudent!.averages.key_word_accuracy_score,
        completion_time: selectedStudent!.averages.completion_time,
        missed_words: 0,
      });
    } else {
      const idx = selectedStudent?.submissions.findIndex(
        (sa) => sa.assignment === selectedAssignment?.getId(),
      );
      if (idx === -1 || !idx) return;
      setAverages({
        grade: selectedStudent?.submissions[idx].grade,
        completion_score: selectedStudent?.submissions[idx].completion_score,
        correctness_score: selectedStudent?.submissions[idx].correctness_score,
        key_word_accuracy_score:
          selectedStudent?.submissions[idx].key_word_accuracy_score,
        completion_time: selectedStudent?.submissions[idx].completion_time,
        missed_words: 0,
      });
    }
  }, [
    selectedAssignment,
    selectedStudent,
    gradebook,
    classroom,
    classGradebook,
  ]);

  const displayMissedWords: DisplayMissedWord[] = useMemo(
    () =>
      countMissedWords(
        missedWords,
        selectedStudent?.username,
        selectedAssignment?.id,
      ),
    [missedWords, selectedStudent, selectedAssignment],
  );

  useEffect(() => {
    if (!classGradebook || !classroom) return;
    const aParam = searchParams.get('assignment');
    if (aParam) {
      setSelectedAssignment(
        aParam === 'all'
          ? undefined
          : classGradebook.assignments.find(
              (a) => a.id.toString() === aParam.toString(),
            ),
      );
    }

    const sParam = searchParams.get('student');
    if (sParam) {
      setSelectedStudent(
        sParam === 'all'
          ? undefined
          : classGradebook.students.find((s) => s.username === sParam),
      );
    }
  }, [searchParams, classroom, classGradebook]);

  // assignment handler
  const handleAssignmentSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    if (!classroom || !classGradebook) return;
    const selectedValue = e.target.value;

    // update React state
    if (selectedValue === 'all') {
      setSelectedAssignment(undefined);
    } else {
      const selectedAssignment = classGradebook.assignments.find(
        (a) => a.id.toString() === selectedValue.toString(),
      );
      setSelectedAssignment(selectedAssignment);
    }

    // sync URL
    const newParams = new URLSearchParams(searchParams);
    if (selectedValue === 'all') {
      newParams.delete('assignment');
    } else {
      newParams.set('assignment', selectedValue);
    }
    setSearchParams(newParams);
  };

  // student handler
  const handleStudentSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    if (!gradebook || !classroom || !classGradebook) return;
    const selectedValue = e.target.value;

    // update React state
    if (selectedValue === 'all') {
      setSelectedStudent(undefined);
    } else {
      const selectedStudent = classGradebook.students.find(
        (s) => s.username === selectedValue,
      );
      setSelectedStudent(selectedStudent);
    }

    // sync URL
    const newParams = new URLSearchParams(searchParams);
    if (selectedValue === 'all') {
      newParams.delete('student');
    } else {
      newParams.set('student', selectedValue);
    }
    setSearchParams(newParams);
  };

  const handleExitAnalytics = () => {
    navigate(-1);
  };

  const handleChangeClassroom = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const id = event.target.value;
    const selectedClassroom = classrooms.find((c) => c.id.toString() === id);
    if (!selectedClassroom) setClassroom(null);
    else setClassroom(Classroom.fromClassroom(selectedClassroom));
    setSelectedAssignment(undefined);
    setSelectedStudent(undefined);
  };

  if (!gradebook) return <AppPage></AppPage>;

  return (
    <AppPage>
      <div className="app-page-content">
        <AnalyticsSidebar
          onExitAnalytics={handleExitAnalytics}
          onAssignmentSelectChange={handleAssignmentSelectChange}
          onStudentSelectChange={handleStudentSelectChange}
          onDownloadProgressReport={() => setShowPdfPopup(true)}
          gradebook={classGradebook ?? undefined}
          districtGradebook={gradebook}
          studentMetrics={studentMetrics}
          averages={averages}
          selectedAssignment={selectedAssignment}
          selectedStudent={selectedStudent}
        />
        <div className="app-main-content">
          <select
            className="select-header"
            defaultValue={classroom?.id}
            onChange={handleChangeClassroom}
          >
            <option value="-1">All Classrooms</option>
            {classrooms.map((c) => (
              <option key={`classroom-option-${c.id}`} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {analyticsType === AnaltyicsType.DISTRICT && (
            <DistrictAnalytics gradebook={gradebook} />
          )}
          {analyticsType === AnaltyicsType.CLASSROOM &&
            classroom &&
            classGradebook && (
              <ClassroomAnalytics
                missedWords={displayMissedWords ?? []}
                gradebook={classGradebook}
              />
            )}
          {analyticsType === AnaltyicsType.ASSIGNMENT &&
            selectedAssignment &&
            classroom &&
            classGradebook && (
              <AssignmentAnayltics
                missedWords={displayMissedWords ?? []}
                assignment={selectedAssignment!}
                students={classGradebook.students}
              />
            )}
          {analyticsType === AnaltyicsType.STUDENT &&
            selectedStudent &&
            classroom &&
            classGradebook && (
              <StudentAnalytics
                gradebook={classGradebook}
                student={selectedStudent!}
                missedWords={displayMissedWords ?? []}
                metrics={studentMetrics}
              />
            )}
          {analyticsType === AnaltyicsType.SUBMISSION && (
            <SubmissionAnalytics missedWords={displayMissedWords ?? []} />
          )}
        </div>
        <Modal
          isOpen={showPdfPopup && !!classroom}
          onClose={() => setShowPdfPopup((prev) => !prev)}
        >
          {classroom && classGradebook && (
            <GeneratePdfPopup gradebook={classGradebook} />
          )}
        </Modal>
      </div>
    </AppPage>
  );
};

export default DistrictAdminAnalyticsPage;
